import { lazy } from 'react';
import type { ReactNode } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import RequirePermission from '@/auth/RequirePermission';
import { Perm } from '@/auth/permissions';
import RequireModule from '@/core/modules/RequireModule';
import { Modules } from '@/core/modules/moduleKeys';
import { stockSections } from './sections';
import type { StockSectionDef } from './pages/StockSectionPage';

// Pages and tabs load when first opened (NFR-04, docs §169).
const StockHomePage = lazy(() => import('./pages/StockHomePage'));
const StockSectionPage = lazy(() => import('./pages/StockSectionPage'));

/**
 * The stock module's routes, spread into the signed-in layout. Every page sits behind `RequireModule`,
 * so an organisation without the module sees the "not enabled" card rather than pages that would only
 * meet 403s. The URL mirrors the menu: the area, then the section, then the leaf.
 */
const guard = (element: ReactNode) => (
  <RequirePermission permission={Perm.Stock.View}>
    <RequireModule module={Modules.Stock}>{element}</RequireModule>
  </RequirePermission>
);

const section = <K extends string>(def: StockSectionDef<K>, path: string): RouteObject =>
  ({ path, element: guard(<StockSectionPage section={def as unknown as StockSectionDef} />) });

export const stockRoutes: RouteObject[] = [
  { path: 'stock', element: guard(<StockHomePage />) },

  { path: 'stock/master', element: <Navigate to="/stock/master/items" replace /> },
  section(stockSections.items, 'stock/master/items/:view?'),
  section(stockSections.stores, 'stock/master/stores/:view?'),

  { path: 'stock/receiving', element: <Navigate to="/stock/receiving/receipts" replace /> },
  section(stockSections.receipts, 'stock/receiving/receipts/:view?'),
  section(stockSections.supplierReturns, 'stock/receiving/supplier-returns/:view?'),
  section(stockSections.landedCosts, 'stock/receiving/landed-costs/:view?'),

  { path: 'stock/issues', element: <Navigate to="/stock/issues/requisitions" replace /> },
  section(stockSections.requisitions, 'stock/issues/requisitions/:view?'),
  section(stockSections.vouchers, 'stock/issues/vouchers/:view?'),

  { path: 'stock/transfers', element: <Navigate to="/stock/transfers/transfers" replace /> },
  section(stockSections.transfers, 'stock/transfers/transfers/:view?'),
  section(stockSections.discrepancies, 'stock/transfers/discrepancies/:view?'),
  section(stockSections.controlledRegister, 'stock/transfers/controlled-register/:view?'),

  { path: 'stock/counts', element: <Navigate to="/stock/counts/counts" replace /> },
  section(stockSections.counts, 'stock/counts/counts/:view?'),

  { path: 'stock/adjustments', element: <Navigate to="/stock/adjustments/adjustments" replace /> },
  section(stockSections.adjustments, 'stock/adjustments/adjustments/:view?'),
  section(stockSections.reorder, 'stock/adjustments/reorder/:view?'),
  section(stockSections.supply, 'stock/adjustments/supply/:view?'),
  section(stockSections.recalls, 'stock/adjustments/recalls/:view?'),

  section(stockSections.reports, 'stock/reports/:view?'),
  section(stockSections.settings, 'stock/settings/:view?'),
];
